import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import CoverFill from './CoverFill';
import { capture } from './Viewer';
import { KIND_LABEL } from '../utils/galleries';
import { useUi } from '../hooks/useUi';

const COPIES = 3;

/**
 * Full-bleed crossfading hero with a looping strip of gallery titles.
 * Drag / swipe anywhere, use ← →, or tap a title to move; tap the active
 * title (or the photo) to open that gallery. The strip is rendered three
 * times and silently re-centred on the middle copy after each move, so it
 * loops forever in both directions.
 *
 * With an `intro` pool, the backdrop first shows those photos (the home
 * page's selected work) and no gallery photos load until the visitor moves
 * the carousel.
 */
const HeroCarousel = ({ slides, intro }) => {
  const n = slides.length;
  const navigate = useNavigate();
  const ui = useUi();

  const [v, setV] = useState(n); // virtual index into the tripled strip
  const [instant, setInstant] = useState(false);
  const [dragX, setDragX] = useState(0);
  const [centers, setCenters] = useState(null);
  const [viewportWidth, setViewportWidth] = useState(0);
  const [showIntro, setShowIntro] = useState(() => Boolean(intro && intro.length));
  const [loaded, setLoaded] = useState(() => (showIntro ? new Set() : new Set([n - 1, 0, 1 % n])));

  const viewportRef = useRef(null);
  const itemRefs = useRef([]);
  const gesture = useRef(null);
  const suppressClick = useRef(false);
  const wheelLock = useRef(0);

  const active = ((v % n) + n) % n;

  const measure = useCallback(() => {
    const vp = viewportRef.current;
    if (!vp) return;
    setViewportWidth(vp.clientWidth);
    setCenters(itemRefs.current.map((el) => (el ? el.offsetLeft + el.offsetWidth / 2 : 0)));
  }, []);

  useLayoutEffect(() => {
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(viewportRef.current);
    document.fonts?.ready.then(measure);
    return () => ro.disconnect();
  }, [measure]);

  // Any move (arrow, wheel, swipe, tapping another title) leaves the intro.
  const startV = useRef(v);
  useEffect(() => {
    if (v !== startV.current) setShowIntro(false);
  }, [v]);

  useEffect(() => {
    if (showIntro) return;
    setLoaded((prev) => {
      const want = [active, (active + 1) % n, (active - 1 + n) % n];
      if (want.every((k) => prev.has(k))) return prev;
      return new Set([...prev, ...want]);
    });
  }, [active, n, showIntro]);

  // After a move lands outside the middle copy, jump back without animating.
  const onTransitionEnd = (e) => {
    if (e.target !== e.currentTarget || e.propertyName !== 'transform') return;
    if (v < n || v >= 2 * n) {
      setInstant(true);
      setV(active + n);
    }
  };

  useEffect(() => {
    if (!instant) return undefined;
    const id = requestAnimationFrame(() => requestAnimationFrame(() => setInstant(false)));
    return () => cancelAnimationFrame(id);
  }, [instant]);

  const step = useCallback((d) => setV((x) => x + d), []);

  // Keyboard + horizontal wheel/trackpad, only while the hero is on screen.
  useEffect(() => {
    const heroVisible = () => window.scrollY < window.innerHeight * 0.5 && !ui.menuOpen && !ui.searchOpen;
    const onKey = (e) => {
      if (!heroVisible() || e.target.closest?.('input, textarea')) return;
      if (e.key === 'ArrowRight') step(1);
      else if (e.key === 'ArrowLeft') step(-1);
    };
    const onWheel = (e) => {
      if (!heroVisible() || Math.abs(e.deltaX) < Math.abs(e.deltaY) || Math.abs(e.deltaX) < 12) return;
      const now = Date.now();
      if (now - wheelLock.current < 700) return;
      wheelLock.current = now;
      step(e.deltaX > 0 ? 1 : -1);
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('wheel', onWheel, { passive: true });
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('wheel', onWheel);
    };
  }, [step, ui.menuOpen, ui.searchOpen]);

  const translate = centers ? viewportWidth / 2 - centers[v] + dragX : 0;

  // Item whose centre sits closest to the viewport centre after dragging by x.
  const nearestTo = (x) => {
    let best = v;
    let bestDist = Infinity;
    centers.forEach((c, i) => {
      const d = Math.abs(c - centers[v] + x);
      if (d < bestDist) {
        bestDist = d;
        best = i;
      }
    });
    return best;
  };

  const onPointerDown = (e) => {
    if (e.button !== 0 || !centers) return;
    gesture.current = { id: e.pointerId, x: e.clientX, y: e.clientY, t: e.timeStamp, axis: null };
    suppressClick.current = false;
  };

  const onPointerMove = (e) => {
    const g = gesture.current;
    if (!g || g.id !== e.pointerId) return;
    const dx = e.clientX - g.x;
    const dy = e.clientY - g.y;
    if (!g.axis) {
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
      g.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      if (g.axis === 'x') capture(e);
    }
    if (g.axis === 'x') {
      suppressClick.current = true;
      setDragX(dx);
    }
  };

  const onPointerUp = (e) => {
    const g = gesture.current;
    gesture.current = null;
    if (!g || g.id !== e.pointerId) return;
    const dx = e.clientX - g.x;
    if (g.axis === 'x') {
      let target = nearestTo(dx);
      const fling = Math.abs(dx) / Math.max(1, e.timeStamp - g.t) > 0.3;
      if (target === v && (Math.abs(dx) > 40 || fling)) target = v + (dx < 0 ? 1 : -1);
      setDragX(0);
      setV(target);
    } else if (!g.axis && !showIntro && !e.target.closest('a, button')) {
      navigate(slides[active].gallery.path);
    }
  };

  const onPointerCancel = () => {
    gesture.current = null;
    setDragX(0);
  };

  const onItemClick = (e, i) => {
    if (suppressClick.current) {
      e.preventDefault();
      return;
    }
    if (i !== v) {
      e.preventDefault();
      setV(i);
    }
  };

  const strip = Array.from({ length: n * COPIES }, (_, i) => slides[i % n]);

  return (
    <section className="hero" aria-roledescription="carousel" aria-label="Galleries">
      <div className="hero__bg">
        {intro && intro.length > 0 && (
          <div className="hero__slide" data-active={showIntro ? '' : undefined} aria-hidden={!showIntro}>
            <CoverFill
              pool={intro}
              eager
              altFor={(img) => img.caption || 'Adventure and travel photograph by Jonny Coe'}
            />
          </div>
        )}
        {slides.map((slide, k) => (
          <div
            key={slide.gallery.slug}
            className="hero__slide"
            data-active={!showIntro && k === active ? '' : undefined}
            aria-hidden={showIntro || k !== active}
          >
            <CoverFill
              pool={slide.pool}
              load={loaded.has(k)}
              eager={!intro && k === 0}
              altFor={(img) => img.caption || `${slide.gallery.title} — photograph by Jonny Coe`}
            />
          </div>
        ))}
      </div>
      <div className="hero__scrim" aria-hidden="true" />
      <div className="hero__scrimTop" aria-hidden="true" />

      <div
        className={`hero__surface${dragX ? ' is-dragging' : ''}`}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
      >
        <div className="hero__carousel">
          <div className="hero__viewport" ref={viewportRef}>
            <div
              className={`hero__track${dragX || instant ? ' is-still' : ''}`}
              data-ready={centers ? '' : undefined}
              style={{ transform: `translate3d(${translate}px, 0, 0)` }}
              onTransitionEnd={onTransitionEnd}
            >
              {strip.map((slide, i) => (
                <Link
                  key={`${i}-${slide.gallery.slug}`}
                  ref={(el) => {
                    itemRefs.current[i] = el;
                  }}
                  to={slide.gallery.path}
                  className="hero__item"
                  data-active={i === v ? '' : undefined}
                  tabIndex={i >= n && i < 2 * n ? 0 : -1}
                  aria-hidden={i < n || i >= 2 * n}
                  onClick={(e) => onItemClick(e, i)}
                  onFocus={() => i >= n && i < 2 * n && setV(i)}
                  draggable={false}
                >
                  <span className="hero__itemTitle">{slide.gallery.title}</span>
                  <span className="hero__itemMeta">
                    {slide.pool.length}
                    <span className="hero__metaRule" aria-hidden="true" />
                    {KIND_LABEL[slide.gallery.kind]}
                  </span>
                </Link>
              ))}
            </div>
          </div>
          <p className="sr-only" aria-live="polite">
            {slides[active].gallery.title}, {KIND_LABEL[slides[active].gallery.kind]} ({active + 1} of {n})
          </p>
        </div>
      </div>

      <div className="hero__baseline" aria-hidden="true">
        <div className="hero__progress" style={{ transform: `scaleX(${(active + 1) / n})` }} />
      </div>
    </section>
  );
};

export default HeroCarousel;
